import DownloadIcon from 'Components/Icons/DownloadIcon'
import RemoveIcon from 'Components/Icons/RemoveIcon'
import { getDocumentDownloadUrlApi } from 'Constants'
import { Context } from 'Context/Context'
import { Upload } from 'lucide-react'
import React, { useContext, useState } from 'react'
import { toast } from 'react-toastify'
import QcrLoader from 'Components/Icons/QcrLoader';

function DocumentsSection({
    name,
    uploadDocuments,
    caseDetails,
    removeDoc,
    isCaseOpen
}) 
{
    const {makeApiCallFunc} = useContext(Context);
    const [showLoadingG, setShowLoadingG] = useState(false);
    const [downloadingFile, setDownloadingFile] = useState(null);

    // Documents/video used to be stored on S3 with ACL: "public-read", so
    // `f.location` was itself a permanent public download link. Uploads are
    // now private, so a fresh, access-checked, short-lived URL has to be
    // requested for each download instead of linking straight to `location`.
    // fileName is a display label, not guaranteed unique within a case -
    // location is, so it's sent too to disambiguate duplicate filenames.
    const downloadDoc = (fileName, location) => {
        // window.open must happen synchronously inside the click handler or
        // most browsers' popup blockers silently swallow it - calling it
        // from the async success callback below (after the network
        // round-trip for the signed URL) would get blocked far more often
        // than not. Open a blank tab now, point it at the real URL once we
        // have it. Not passing 'noopener' here since the reference is
        // needed to redirect it - opener is cleared manually once done.
        const newTab = window.open('', '_blank');
        setDownloadingFile(fileName);
        makeApiCallFunc(
            getDocumentDownloadUrlApi,
            { caseId: caseDetails?._id, documentTypeName: name, fileName, location },
            ({ data }) => {
                setDownloadingFile(null);
                if (data?.url) {
                    if (newTab) {
                        try { newTab.opener = null; } catch (e) {}
                        newTab.location.href = data.url;
                    } else {
                        // The blank tab itself got blocked - we still have a
                        // valid URL, so fall back to same-tab navigation
                        // rather than just failing.
                        window.location.href = data.url;
                    }
                } else {
                    if (newTab) newTab.close();
                    toast.error('Could not download ' + fileName);
                }
            },
            (error) => {
                setDownloadingFile(null);
                console.log(error);
                if (newTab) newTab.close();
                toast.error('Could not download ' + fileName);
            }
        );
    }
    
    return (
        <div className='flex flex-col mt-2 w-full font-plexsans'>
            {caseDetails?.[name]?.length ?
            <>
            {isCaseOpen &&
                <div className='flex w-full justify-between items-center gap-3'>
                <div className='text-[13px] text-[rgba(20,33,61,.55)]'>{name === "videoDocument" ? "Optionally upload a video statement." : "Optionally upload supporting documents."}
                </div>

                <div className='flex justify-end items-center flex-none'>
                    <label htmlFor={name} className='cursor-pointer flex items-center gap-1.5 bg-tan text-white hover:bg-tanDark px-3 py-2 rounded-lg text-[13px] font-semibold whitespace-nowrap'>
                        <Upload size={14}/>
                        {name === "videoDocument" ? "Replace video file" :  "Upload more"}
                    </label>
                    <input
                        type='file' 
                        id={name}
                        name
                        accept = {name === "videoDocument" ? ".mp4" : "image/*,.doc,.docx,.pdf"}
                        multiple = "multiple"
                        onChange = {(e) => {uploadDocuments(e,name);}}
                        className="hidden"
                        /></div>
                </div>
                }
                <div className='mt-2'>
                    <div className='text-[13px] font-semibold text-navy mb-1.5'>{name === "videoDocument" ? "Video statement" : "Documents"} uploaded by you
                     </div>
                     <div className='max-h-[200px] overflow-y-auto rounded-[10px] border border-[rgba(20,33,61,.12)]'>
                        <table className="table-auto w-full">
                            <tbody>
                                {caseDetails?.[name]?.map((f, key1) => (
                                <tr key={key1} className="border-b border-[rgba(20,33,61,.08)] last:border-b-0">
                                    <td className="px-3 py-2 text-center text-[13px] text-[rgba(20,33,61,.5)] w-10">{key1 + 1}</td>
                                    <td className="px-3 py-2 text-[13px] text-navy overflow-hidden whitespace-nowrap text-ellipsis max-w-0">
                                    {f.fileName}
                                    </td>
                                    <td className="px-3 py-2 text-center w-12">
                                    <div
                                        onClick={() => downloadingFile ? null : downloadDoc(f.fileName, f.location)}
                                        className="cursor-pointer flex items-center justify-center h-full w-full"
                                    >
                                        {downloadingFile === f.fileName ?
                                        <QcrLoader variant="draw" size={20} />
                                        :
                                        <DownloadIcon />}
                                    </div>
                                    </td>
                                    <td className="px-3 py-2 text-center w-12">
                                    {isCaseOpen && (
                                        <div
                                        className="cursor-pointer flex items-center justify-center h-full w-full"
                                        onClick={() =>
                                        {
                                            setShowLoadingG(f.fileName);
                                            removeDoc(f.fileName, {
                                            [name]: caseDetails[name].filter(
                                                (doc) => doc.fileName !== f.fileName
                                            ),
                                            },
                                            () => {setShowLoadingG(null);})
                                           }
                                        }
                                        >
                                        {showLoadingG === f.fileName ?
                                        <QcrLoader variant="draw" size={20} />
                                        :

                                        <RemoveIcon />
                            }
                                        </div>
                                    )}
                                    </td>
                                </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>

                </div>
            </>
            :
            (
                isCaseOpen &&
                <div className='flex flex-col w-full gap-2'>
                <div className='text-[13px] text-[rgba(20,33,61,.55)]'>{name === "videoDocument" ? "Optionally upload a video statement." : "Optionally upload supporting documents."}
                </div>
                <label htmlFor={name} className='cursor-pointer flex items-center justify-center gap-2 border border-[rgba(20,33,61,.15)] text-navy hover:border-tan hover:text-tan px-4 py-3 rounded-lg text-[13px] font-semibold transition-colors'>
                    <Upload size={14}/>
                    {name === "videoDocument" ? "Add a video statement" :  "Upload supporting documents"}
                </label>
                <input 
                    type='file' 
                    id={name}
                    name
                    accept = {name === "videoDocument" ? ".mp4" : "image/*,.doc,.docx,.pdf"}
                    multiple = "multiple"
                    onChange = {(e) => uploadDocuments(e, name)}
                    className="hidden"
                    />
            </div>
            )
            
            }
        </div>
    )
}

export default DocumentsSection
